import { useQuery } from '@tanstack/react-query';
import type { CourseMapRoot } from '@/components/CourseMap/coursemap.types';
import { supabase } from '@/lib/supabase';
import type { Course, User } from '@/lib/types';

const schoolId = import.meta.env.VITE_SCHOOL_ID;

export const getUsedCoursesMapIds = (tree: any) => {
	if (!tree) return null;
	const trackers = [] as any;
	if (tree.children) {
		tree.children.forEach((child: any) => {
			if (!child.isDiscipline && !child.id.includes('Spacer')) {
				trackers.push(child.tracker);
			}
			trackers.push(...(getUsedCoursesMapIds(child) ?? []));
		});
	}
	const unique = [...new Set(trackers)];
	return unique;
};

export const getUnusedCourses = (coursemap: any, courses: any) => {
	const mapIDs = getUsedCoursesMapIds(coursemap);
	const courseIds = courses.map((course: any) => course.course_id);

	if (mapIDs && courseIds) {
		const idList = courseIds.filter((courseId: any) => !mapIDs.includes(courseId));

		return courses.filter((course: any) => idList.includes(course.course_id));
	}
	return null;
};

interface IUseCourseCount {
	count: number;
	coursemap: CourseMapRoot;
	courses: Pick<Course, 'course_id' | 'course_name'>[];
	unusedCourses: Pick<Course, 'course_id' | 'course_name'>[];
}

export function useCourseCount() {
	return useQuery<IUseCourseCount>({
		queryKey: ['course-count-aggregate', schoolId],
		queryFn: async () => {
			// Get courses count and list
			const {
				count,
				data: courses,
				error: coursesError,
			} = await supabase
				.from('courses')
				.select('course_id, course_name', { count: 'exact' })
				.eq('school_id', schoolId)
				.order('course_name', { ascending: true });

			if (coursesError) throw coursesError;

			// Get course map
			const { data: courseMapData, error: mapError } = await supabase
				.from('course_map')
				.select('course_map_jsonb')
				.eq('isDraft', false)
				.eq('school_id', schoolId)
				.maybeSingle();

			if (mapError) throw mapError;

			const coursemap = (courseMapData?.course_map_jsonb as CourseMapRoot | null) ?? {
				name: '',
				id: '',
				tracker: 'root' as const,
				children: [],
			};

			const coursesList = (courses ?? []) as Pick<Course, 'course_id' | 'course_name'>[];

			const res: IUseCourseCount = {
				count: count ?? 0,
				courses: coursesList,
				coursemap,
				unusedCourses: getUnusedCourses(coursemap, coursesList),
			};

			return res;
		},
		staleTime: Infinity,
		refetchOnMount: 'always',
	});
}

export function useUnassignedUsers() {
	return useQuery<{
		users: Pick<User, 'user_id' | 'user_first' | 'user_last' | 'user_role'>[];
	}>({
		queryKey: ['unassigned-users', schoolId],
		queryFn: async () => {
			// Get all users with their course counts
			const { data: users, error: usersError } = await supabase
				.from('users')
				.select(
					`
					user_id,
					user_first,
					user_last,
					user_role,
					courses_users (
						courses_users_id
					)
				`
				)
				.eq('school_id', schoolId)
				.order('user_first', { ascending: true });

			if (usersError) throw usersError;

			// Filter users with no courses (courses_users array is empty or null)
			const unassignedUsers = (users ?? [])
				.filter((user: any) => !user.courses_users || user.courses_users.length === 0)
				.map((user: any) => ({
					user_id: user.user_id,
					user_first: user.user_first,
					user_last: user.user_last,
					user_role: user.user_role,
				}));

			const res = {
				users: unassignedUsers as Pick<
					User,
					'user_id' | 'user_first' | 'user_last' | 'user_role'
				>[],
			};

			return res;
		},
		staleTime: Infinity,
		refetchOnMount: 'always',
	});
}

export function useUserCount() {
	return useQuery<{ count: number }>({
		queryKey: ['user-count', schoolId],
		queryFn: async () => {
			const { count, error } = await supabase
				.from('users')
				.select('*', { count: 'exact', head: true })
				.eq('school_id', schoolId);

			if (error) throw error;

			const res = {
				count: count ?? 0,
			};

			return res;
		},
		staleTime: Infinity,
		refetchOnMount: 'always',
	});
}

interface SubmissionStats {
	total: number;
	approved: number;
	pendingDept: number;
	pendingAdmin: number;
	rejected: number;
}

export function useSubmissionStats() {
	return useQuery<SubmissionStats>({
		queryKey: ['submission-stats', schoolId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from('submissions')
				.select('admin_approval, dept_approval')
				.eq('school_id', schoolId);

			if (error) throw error;

			const submissions = data ?? [];
			return {
				total: submissions.length,
				approved: submissions.filter(
					(s) => s.admin_approval === 'approved' && s.dept_approval === 'approved'
				).length,
				pendingDept: submissions.filter((s) => s.dept_approval === 'waiting').length,
				pendingAdmin: submissions.filter(
					(s) => s.dept_approval === 'approved' && s.admin_approval === 'waiting'
				).length,
				rejected: submissions.filter(
					(s) => s.admin_approval === 'rejected' || s.dept_approval === 'rejected'
				).length,
			};
		},
		staleTime: Infinity,
		refetchOnMount: 'always',
	});
}

interface DisciplineStat {
	id: string;
	name: string;
	color: string;
	count: number;
}

export function useDisciplineStats() {
	return useQuery<DisciplineStat[]>({
		queryKey: ['discipline-stats', schoolId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from('disciplines')
				.select(
					`
					id,
					name,
					color,
					courses_disciplines (courses_disciplines_id)
				`
				)
				.eq('school_id', schoolId)
				.order('name', { ascending: true });

			if (error) throw error;

			return (data ?? []).map((d) => ({
				id: d.id,
				name: d.name,
				color: d.color ?? '#666666',
				count:
					(d as unknown as { courses_disciplines: Array<unknown> }).courses_disciplines?.length ??
					0,
			}));
		},
		staleTime: Infinity,
		refetchOnMount: 'always',
	});
}

interface CourseStatusStats {
	ap: number;
	draft: number;
	archived: number;
}

export function useCourseStatusStats() {
	return useQuery<CourseStatusStats>({
		queryKey: ['course-status-stats', schoolId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from('courses')
				.select('is_ap, isDraft, is_archived, is_saved_draft')
				.eq('school_id', schoolId);

			if (error) throw error;

			const courses = data ?? [];
			return {
				ap: courses.filter((c) => c.is_ap).length,
				draft: courses.filter((c) => c.isDraft || c.is_saved_draft).length,
				archived: courses.filter((c) => c.is_archived).length,
			};
		},
		staleTime: Infinity,
		refetchOnMount: 'always',
	});
}

interface UserRoleStats {
	admins: number;
	deptChairs: number;
	teachers: number;
}

export function useUserRoleStats() {
	return useQuery<UserRoleStats>({
		queryKey: ['user-role-stats', schoolId],
		queryFn: async () => {
			const { data, error } = await supabase
				.from('users')
				.select('user_role')
				.eq('school_id', schoolId);

			if (error) throw error;

			const users = data ?? [];
			return {
				admins: users.filter((u) => u.user_role === 'admin').length,
				deptChairs: users.filter((u) => u.user_role === 'dept').length,
				teachers: users.filter((u) => u.user_role === 'user').length,
			};
		},
		staleTime: Infinity,
		refetchOnMount: 'always',
	});
}

export function useUnassignedCourses() {
	return useQuery<{
		count: number;
		courses: Array<Pick<Course, 'course_name'> & { courses_users: Array<{ user_id: string }> }>;
	}>({
		queryKey: ['unassigned-course-count', schoolId],
		queryFn: async () => {
			// Get all courses with their users
			const { data: courses, error } = await supabase
				.from('courses')
				.select(
					`
					course_name,
					courses_users (
						user_id
					)
				`
				)
				.eq('school_id', schoolId);

			if (error) throw error;

			// Filter courses with no users
			const unassignedCourses = (courses ?? []).filter(
				(course: any) => !course.courses_users || course.courses_users.length === 0
			);

			return {
				count: unassignedCourses.length,
				courses: unassignedCourses as Array<
					Pick<Course, 'course_name'> & { courses_users: Array<{ user_id: string }> }
				>,
			};
		},
		staleTime: Infinity,
		refetchOnMount: 'always',
	});
}
